import { useQuery } from "@apollo/client"
import { Box, IconButton, VStack } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ui/src/components/lng/withTranslate"
import CardLink from "@pathwright/ux/src/components/card/CardLink"
import CardLinks from "@pathwright/ux/src/components/card/CardLinks"
import Link from "@pathwright/ux/src/components/link/Link"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import ProfileUserCard from "../../profile/ProfileUserCard"
import getStripePaymentPortalHref from "../../stripe/utils/getStripePaymentPortalHref"
import { getIsSubscriptionActive } from "../../subscription/utils"
import { useUserCommunityContext } from "../../user/UserCommunityContext"
import USER_SCHOOL_SUBSCRIPTION_QUERY from "../../user/graphql/user-school-subscription-query"
import HomeSettingsNotifications from "./UserAugmentationNotificationsSettings"
import { useUserPaymentHistoryItems } from "./UserAugmentationPaymentHistory"

function useManageSubscriptionCardLink() {
  const { t } = useTranslate()
  const { communityGroupSubscriptions, isCommunityGroupOwner } =
    useUserCommunityContext()
  const isCommunityGroupSubscriptionMember =
    communityGroupSubscriptions.length > 0

  const userSubscriptionQuery = useQuery(USER_SCHOOL_SUBSCRIPTION_QUERY)
  const userSubscription =
    userSubscriptionQuery.data?.me?.school_subscription?.subscription
  const isSchoolSubscriptionOwner = getIsSubscriptionActive(userSubscription)

  // Conditionally  link out to the Stripe portal when user can manage
  // a community group subscription.
  const linkProp = isCommunityGroupOwner
    ? {
        href: getStripePaymentPortalHref()
      }
    : {
        to: isCommunityGroupSubscriptionMember
          ? "/subscribe"
          : "/manage/billing"
      }

  return isCommunityGroupSubscriptionMember || isSchoolSubscriptionOwner ? (
    <CardLink
      icon="credit-card"
      label={t("Manage subscription")}
      {...linkProp}
    />
  ) : null
}

function UserAugmentation() {
  const { me } = usePathwrightContext()
  const hasPaymentHistory = useUserPaymentHistoryItems()?.length > 0

  const manageSubscriptionCardLink = useManageSubscriptionCardLink()

  return (
    <VStack w="100%" minH="100%" spacing={4} px={2}>
      <Box pos="relative">
        <ProfileUserCard user={me} />
        <IconButton
          as={Link}
          aria-label="Edit Profile"
          icon={<Pathicon icon="edit" />}
          // @ts-expect-error: TODO: see if we can fix typing when using as={Link}
          relativeTo="profile"
          variant="ghost"
          cursor="pointer"
          isRound
          h="8"
          minW="8"
          pos="absolute"
          top={2}
          right={2}
        />
      </Box>
      <CardLinks>
        <HomeSettingsNotifications />
        {hasPaymentHistory && (
          <CardLink
            icon="credit-card"
            label="View payment history"
            relativeTo="payment-history"
          />
        )}
        {manageSubscriptionCardLink}
        <CardLink
          icon="lock"
          label={me.no_password_set ? "Create password" : "Change password"}
          relativeTo="password"
        />
      </CardLinks>
      <Link
        to="/auth/sign-out/"
        leftIcon={<Pathicon icon="arrow-up-line" transform="rotate(-90deg)" />}
        colorScheme="brand"
        variant="solid"
        width="full"
      >
        Sign out
      </Link>
    </VStack>
  )
}

export default UserAugmentation
